/**
 * THE CARD ON THE TABLE.
 *
 * Specification: ../test/table-token.spec.ts. Master plan §4 M11, session
 * model 2.
 *
 * `./token.ts` mints a code for ONE CHECK, which is the right answer for "show
 * this guest their bill" and the wrong one for a sticker: it can only exist
 * after a server has opened a check on the till, so a guest cannot sit down,
 * scan and start a tab. `GuestCodeDialog.vue` already says why a printed card
 * is wrong for it — it names one check, and next Tuesday it names a stranger's.
 *
 * So this is a SECOND KIND OF CODE, not a change to the first.
 *
 *   A TABLE CARD SAYS WHERE YOU ARE SITTING. No order, no check, nowhere in the
 *   payload to put one. It is printed once and laminated.
 *
 * WHAT MAKES A LONG-LIVED CODE SAFE IS THAT IT BUYS ONE EXCHANGE. Scanning it
 * yields a short-lived, check-scoped token of exactly the kind that already
 * exists — so the bill, the quote seal and the payment path are untouched and
 * still cannot be reached with anything laminated to a table. The card's whole
 * authority is "this is table 12 at this venue", and the controls that matter
 * (rate limits, whether a venue allows guests to open their own tab at all)
 * live at the exchange, where a venue can change them without reprinting.
 *
 * WHY THE FIELDS ARE THE SIZE THEY ARE — the same argument as `./token.ts`, and
 * it is a product decision rather than a saving. A shorter code is not a smaller
 * file; it is a code that SCANS FROM FURTHER AWAY AND IN WORSE LIGHT, off a
 * table, at an angle, by somebody who will give up after two tries.
 *
 *   venue        16 bytes, the uuid itself
 *   name length   1 byte
 *   table name   up to 32 bytes, UTF-8
 *   issued        6 bytes, milliseconds
 *   expires       6 bytes, milliseconds
 *   nonce         4 bytes
 *   MAC          16 bytes, truncated from SHA-256 (RFC 2104 §5)
 *
 * "12" comes to 51 bytes and 68 characters — a version-4 code at level M. The
 * longest name the format allows comes to 98 characters, still inside a
 * version 5.
 */

import { fromBase64Url, toBase64Url } from "@gf/kernel/crypto";

export interface TableTokenClaims {
  readonly venueId: string;
  /**
   * What the venue calls this table. `12`, `T4`, `terasa 3`, `bar left`.
   *
   * TEXT, NOT A UUID, because a venue has no table registry to look one up in —
   * `tableRef` is a free string on an order everywhere else in this system, and
   * inventing an id here would mean inventing the table list to go with it.
   */
  readonly tableRef: string;
  /** Milliseconds since the epoch, from whichever clock printed the card. */
  readonly issuedAt: number;
  /** Milliseconds since the epoch. Inside the MAC, so it cannot be extended. */
  readonly expiresAt: number;
  /**
   * Makes two cards for the same table different.
   *
   * Without it, reprinting table 12's card produces byte-identical output — so
   * a venue that reprinted in order to kill the old cards would have killed
   * nothing, and a scanner's cache would answer from the previous set.
   */
  readonly nonce: number;
}

export type TableTokenFailure =
  | "MALFORMED"
  | "BAD_SIGNATURE"
  | "EXPIRED"
  /** Issued far enough in the future that it is not clock drift. */
  | "NOT_YET_VALID"
  /** The card is for a different venue — a scanner pointed at the wrong QR. */
  | "WRONG_VENUE";

export type TableTokenResult =
  | { readonly ok: true; readonly claims: TableTokenClaims }
  | { readonly ok: false; readonly reason: TableTokenFailure };

/**
 * How long a card is good for.
 *
 * A year. Longer than the check-scoped code by three orders of magnitude, and
 * deliberately not forever: a venue that reprints must be able to retire the
 * old set, and a card photographed today must not work in five years when the
 * room has been rebuilt.
 */
export const TABLE_TOKEN_TTL_MS = 365 * 86_400_000;

/**
 * How much clock skew is forgiven on the ISSUED side.
 *
 * Thirty seconds ahead is a tablet that is slightly fast; an hour ahead is
 * neither that nor an accident. The expiry side gets no such grace — erring
 * toward closing a window early is the safe direction.
 */
const FUTURE_SKEW_MS = 60_000;

/**
 * The longest table name that fits on a card.
 *
 * Bytes rather than characters, because the payload is bytes and "Salónek" is
 * eight of them for seven letters. Thirty-two is roomy for every real table
 * name and still leaves the longest possible code inside a version-5 QR.
 */
const MAX_TABLE_BYTES = 32;

const VENUE_BYTES = 16;
const TAIL_BYTES = 16; // issued(6) + expires(6) + nonce(4)
const MAC_BYTES = 16;

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });

async function key(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

const uuidToBytes = (uuid: string): Uint8Array => {
  const hex = uuid.replaceAll("-", "");
  if (hex.length !== 32) throw new Error(`not a uuid: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
};

const bytesToUuid = (bytes: Uint8Array): string => {
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** Six bytes, big-endian. Milliseconds fit until long after any of this matters. */
const writeMillis = (into: Uint8Array, at: number, value: number): void => {
  let v = value;
  for (let i = 5; i >= 0; i--) {
    into[at + i] = v % 256;
    v = Math.floor(v / 256);
  }
};

const readMillis = (from: Uint8Array, at: number): number => {
  let v = 0;
  for (let i = 0; i < 6; i++) v = v * 256 + from[at + i]!;
  return v;
};

export interface MintTableArgs {
  readonly venueId: string;
  readonly tableRef: string;
  readonly secret: string;
  readonly now: Date;
  readonly nonce: number;
  readonly ttlMs?: number;
}

/**
 * Print a card. Pure apart from the MAC; no network, no storage.
 *
 * The name is trimmed here and nowhere else, so `" 12 "` and `"12"` are the
 * same table. A venue typing a trailing space into a console field must not get
 * a card that opens a second, parallel table nobody can deliver food to.
 */
export async function mintTableToken(args: MintTableArgs): Promise<string> {
  const tableRef = args.tableRef.trim();
  if (tableRef === "") {
    throw new Error("table card: a table name is required — a card that says nowhere is useless");
  }

  const nameBytes = encoder.encode(tableRef);
  if (nameBytes.length > MAX_TABLE_BYTES) {
    // Refused where somebody is looking, rather than producing a card that
    // scans to nothing or silently naming a different table.
    throw new Error(
      `table card: "${tableRef}" is ${String(nameBytes.length)} bytes; the limit is ${String(MAX_TABLE_BYTES)}`,
    );
  }

  const issuedAt = args.now.getTime();
  const expiresAt = issuedAt + (args.ttlMs ?? TABLE_TOKEN_TTL_MS);

  /**
   * THE LENGTH IS INSIDE THE MAC, not just the name.
   *
   * Otherwise a forger could move the boundary — shortening the name by one
   * byte and stealing that byte for the timestamp that follows — and produce a
   * different, still-consistent reading of the same bytes.
   */
  const payload = new Uint8Array(VENUE_BYTES + 1 + nameBytes.length + TAIL_BYTES);
  payload.set(uuidToBytes(args.venueId), 0);
  payload[VENUE_BYTES] = nameBytes.length;
  payload.set(nameBytes, VENUE_BYTES + 1);

  const tail = VENUE_BYTES + 1 + nameBytes.length;
  writeMillis(payload, tail, issuedAt);
  writeMillis(payload, tail + 6, expiresAt);
  new DataView(payload.buffer).setUint32(tail + 12, args.nonce >>> 0);

  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", await key(args.secret), payload));

  const token = new Uint8Array(payload.length + MAC_BYTES);
  token.set(payload, 0);
  token.set(mac.subarray(0, MAC_BYTES), payload.length);
  return toBase64Url(token);
}

export interface VerifyTableArgs {
  readonly token: string;
  readonly secret: string;
  readonly now: Date;
  /** Refuse a card minted for a different venue, even if it verifies. */
  readonly venueId?: string;
}

/**
 * Read a card and say what it is for.
 *
 * ORDER MATTERS: the MAC is verified BEFORE anything inside it is believed.
 * Refusing on an expiry read out of an unverified payload would tell a forger
 * which part of his lie was believed, and let him learn the format by watching
 * which refusal each attempt produced.
 */
export async function verifyTableToken(args: VerifyTableArgs): Promise<TableTokenResult> {
  let raw: Uint8Array;
  try {
    raw = fromBase64Url(args.token);
  } catch {
    return { ok: false, reason: "MALFORMED" };
  }

  // Enough for a venue, a length byte, at least one character of name, the
  // timestamps and the MAC.
  const floor = VENUE_BYTES + 1 + 1 + TAIL_BYTES + MAC_BYTES;
  if (raw.length < floor || raw.length > VENUE_BYTES + 1 + MAX_TABLE_BYTES + TAIL_BYTES + MAC_BYTES) {
    return { ok: false, reason: "MALFORMED" };
  }

  const nameLength = raw[VENUE_BYTES]!;
  const expected = VENUE_BYTES + 1 + nameLength + TAIL_BYTES + MAC_BYTES;
  // The declared length has to account for every byte present. A payload that
  // says otherwise is malformed, and saying so before the MAC leaks nothing:
  // it is a statement about the envelope, not about the contents.
  if (raw.length !== expected) return { ok: false, reason: "MALFORMED" };

  /**
   * Copied into a fresh `ArrayBuffer` rather than sliced as a view.
   *
   * WebCrypto's `BufferSource` requires a plain `ArrayBuffer` backing, and the
   * decoded token may be a view onto a shared pool — the same reason
   * `apps/hub/src/trust.ts` copies its base64url decode. It also makes
   * `DataView(payload.buffer)` below read the payload rather than whatever the
   * pool happens to hold around it.
   */
  const payload = new Uint8Array(raw.subarray(0, raw.length - MAC_BYTES));
  const mac = raw.subarray(raw.length - MAC_BYTES);

  /**
   * Compared byte by byte in CONSTANT TIME, the same way `./token.ts` does it
   * and for the same reason: `crypto.subtle.verify` expects the full digest and
   * this MAC is truncated, so the comparison is explicit — and it must not
   * short-circuit, which is why it accumulates a difference rather than
   * returning early.
   *
   * Written identically to its sibling on purpose. Two spellings of "is this
   * MAC right" is the duplication this system cannot survive.
   */
  const expectedMac = new Uint8Array(
    await crypto.subtle.sign("HMAC", await key(args.secret), payload),
  ).subarray(0, MAC_BYTES);

  let difference = 0;
  for (let i = 0; i < MAC_BYTES; i++) difference |= mac[i]! ^ expectedMac[i]!;
  if (difference !== 0) return { ok: false, reason: "BAD_SIGNATURE" };

  let tableRef: string;
  try {
    tableRef = decoder.decode(payload.subarray(VENUE_BYTES + 1, VENUE_BYTES + 1 + nameLength));
  } catch {
    // Verified bytes that are not valid UTF-8 mean a card minted by something
    // that is not this module. Malformed is the honest word.
    return { ok: false, reason: "MALFORMED" };
  }

  const tail = VENUE_BYTES + 1 + nameLength;
  const claims: TableTokenClaims = {
    venueId: bytesToUuid(payload.subarray(0, VENUE_BYTES)),
    tableRef,
    issuedAt: readMillis(payload, tail),
    expiresAt: readMillis(payload, tail + 6),
    nonce: new DataView(payload.buffer).getUint32(tail + 12),
  };

  const now = args.now.getTime();
  if (claims.issuedAt > now + FUTURE_SKEW_MS) return { ok: false, reason: "NOT_YET_VALID" };
  if (claims.expiresAt <= now) return { ok: false, reason: "EXPIRED" };
  if (args.venueId !== undefined && claims.venueId !== args.venueId) {
    return { ok: false, reason: "WRONG_VENUE" };
  }

  return { ok: true, claims };
}
